import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../../../components/ui/dialog';
import { Input } from '../../../../../../components/ui/input';
import { Label } from '../../../../../../components/ui/label';
import { Button } from '../../../../../../components/ui/button';
import { NumberInput } from '../../../../../../components/ui/NumberInput';
import { FileUpload } from '../../../../../../components/ui/FileUpload';
import { SearchableSelect } from '../../../../../../components/ui/SearchableSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../../components/ui/select';
import { AntdDate } from '../../../../../../components/ui/antd-date';
import AddNewItemDialog from '../../../../../../components/ui/AddNewItemDialog';
import dayjs from 'dayjs';
import { X, ThumbsUp, ThumbsDown, ArrowLeft } from 'lucide-react';
import { createNDTRepairLog, updateNDTRepairLog } from '../../../../../../redux/ProductionData/actions';
import { AddSpecification, getPreSignedUrlToUpload } from '../../../../../../redux/Project/specificationSaga';
import { getListMaterialCategory, AddMaterialCategory } from '../../../../../../redux/Project/materialSaga';
import { SPEC_TAG_TYPES } from '../../../../constants/specificationConstants';

interface AddRepairLogDialogProps {
  isOpen: boolean;
  onClose: () => void;
  weldNumber?: string;
  weld: any;
  initialData?: Partial<RepairLogData>;
}

export interface RepairLogData {
  id?: number;
  project_id: number;
  pipe_id: number;
  weld_id: number;
  date?: string;
  shift?: string;
  weldersNo?: string;
  welderRepairNo?: string;
  location?: string;
  defectType?: string;
  acceptedWorkmanship?: 'Yes' | 'No';
  defectLocationStart: string | number;
  defectLocationEnd: string | number;
  defectLength: string | number;
  defectDepth: string | number;
  defectHeight: string | number;
  comments: string;
  rootCause: string;
  documents: File[];
  documentTags?: any[];
  pipeDia?: number;
  wallThickness?: number;
  wpsNumber?: string;
  oclockLocation?: string;
  repair?: string;
  rca?: string;
  ndtIndicationDocId?: number[];
  ndtIndicationDocuments?: any[];
  passName?: string;
  evaluation?: 'Accept' | 'Reject';
  criticalWeld?: 'Yes' | 'No';
  correctiveActions?: string;
  time?: string;
}

export const AddRepairLogDialog: React.FC<AddRepairLogDialogProps> = ({
  isOpen,
  onClose,
  weld,
  weldNumber,
  initialData
}) => {
  const dispatch = useDispatch();
  const { createNDTRepairLogLoading, updateNDTRepairLogLoading, createNDTRepairLogSuccess, updateNDTRepairLogSuccess } = useSelector((state: any) => state.productionData);
  
  // Get user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
 
  const weldId = weld?.weldNumber || weld?.id || 0;
  const projectId = weld?.projectId || weld.selectedPipe?.projectId || 0;
  const pipeId = weld?.selectedPipe?.pipeId || null;
  const [isLoading, setIsLoading] = useState(false);
  // Determine if we're saving (either create or update)
  const isSaving = createNDTRepairLogLoading || updateNDTRepairLogLoading;
  
  // Ref to track initial load to prevent auto-calculation from overriding saved o'clock location in edit mode
  const isInitialLoad = useRef(true);

  // Default empty form data for new entries
  const getDefaultFormData = (): RepairLogData => ({
    project_id: projectId,
    pipe_id: pipeId,
    weld_id: weldId,
    date: dayjs().format('YYYY-MM-DD'),
    shift: '',
    weldersNo: '',
    welderRepairNo: '',
    location: '',
    defectType: '',
    acceptedWorkmanship: undefined,
    defectLocationStart: '',
    defectLocationEnd: '',
    defectLength: '',
    defectDepth: '',
    defectHeight: '',
    comments: '',
    rootCause: '',
    documents: [],
    documentTags: [],
    pipeDia: (weld as any).pipe_diameter || 0,
    wallThickness: (weld as any).wall_thickness || (weld as any).thickness || 0,
    wpsNumber: weld.wpsNumber || '',
    oclockLocation: '',
    repair: '',
    rca: '',
    ndtIndicationDocId: [],
    passName: '',
    evaluation: undefined,
    criticalWeld: undefined,
    correctiveActions: '',
    time: ''
  });

  const [date, setDate] = useState<string>(initialData?.date || dayjs().format('YYYY-MM-DD'));
  const [formData, setFormData] = useState<RepairLogData>(getDefaultFormData());

  // Update form data when initialData changes (for edit mode) or when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
       // console.log('weld', weld);
        // Edit mode - populate form with initialData
        // Set flag to prevent auto-calculation from overriding saved o'clock location
        isInitialLoad.current = true;
        setDate(initialData.date || dayjs().format('YYYY-MM-DD'));
        setFormData({
          id: initialData.id,
          project_id: projectId,
          pipe_id: pipeId,
          weld_id: weldId,
          date: initialData.date || dayjs().format('YYYY-MM-DD'),
          shift: initialData.shift || '',
          weldersNo: initialData.weldersNo || '',
          welderRepairNo: initialData.welderRepairNo || '',
          location: initialData.location || '',
          defectType: initialData.defectType || '',
          acceptedWorkmanship: initialData.acceptedWorkmanship,
          defectLocationStart: initialData.defectLocationStart || '',
          defectLocationEnd: initialData.defectLocationEnd || '',
          defectLength: initialData.defectLength || '',
          defectDepth: initialData.defectDepth || '',
          defectHeight: initialData.defectHeight || '',
          comments: initialData.comments || '',
          rootCause: initialData.rootCause || '',
          documents: initialData.documents || [],
          documentTags: initialData.documentTags || [],
          pipeDia: initialData.pipeDia || (weld as any).pipe_diameter || 20,
          wallThickness: (weld as any).wall_thickness || (weld as any).thickness || 5,
          wpsNumber: weld.wpsNumber || '',
          oclockLocation: initialData.oclockLocation || '',
          repair: initialData.repair || '',
          rca: initialData.rca || '',
          ndtIndicationDocId: initialData.ndtIndicationDocId || [],
          passName: initialData.passName || '',
          evaluation: initialData.evaluation,
          criticalWeld: initialData.criticalWeld,
          correctiveActions: initialData.correctiveActions || '',
          time: initialData.time || ''
        });
        // Reset flag after a short delay to allow auto-calculation on user changes
        setTimeout(() => {
          isInitialLoad.current = false;
        }, 100);
      } else {
        // Add mode - reset to default empty form
        isInitialLoad.current = false; // Allow auto-calculation in add mode
        setDate(dayjs().format('YYYY-MM-DD'));
        setFormData(getDefaultFormData());
      }
    }
  }, [isOpen]);

  // // Close dialog and reset form when save is successful
  // useEffect(() => {
  //   if (isOpen && (createNDTRepairLogSuccess || updateNDTRepairLogSuccess) && !isSaving) {
  //     console.log('createNDTRepairLogSuccess', createNDTRepairLogSuccess);
  //     // Small delay to ensure UI updates are visible
  //     const timer = setTimeout(() => {
  //       // Reset form
  //       setDate(dayjs().format('YYYY-MM-DD'));
  //       setFormData(getDefaultFormData());
  //       setSelectedTags({});
  //       setSelectedFiles([]);
  //       onClose();
  //     }, 100);
  //     return () => clearTimeout(timer);
  //   }
  // }, [createNDTRepairLogSuccess, updateNDTRepairLogSuccess]);

  const [selectedTags, setSelectedTags] = useState<any>({});
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const [isAddingShift, setIsAddingShift] = useState(false);
  const [isAddingDefectType, setIsAddingDefectType] = useState(false);
  const [isAddingLocation, setIsAddingLocation] = useState(false);
  const [isAddingRepair, setIsAddingRepair] = useState(false);
  const [isAddingRca, setIsAddingRca] = useState(false);
  const [isAddingTag, setIsAddingTag] = useState(false);
  
  // Tag types for different categories
  const TAG_TYPES = {
    NDT: SPEC_TAG_TYPES['NDT'],
    SHIFT: 'NDT_REPAIR_LOG_SHIFT',
    DEFECT_TYPE: 'NDT_REPAIR_LOG_DEFECT_TYPE',
    LOCATION: 'NDT_REPAIR_LOG_LOCATION',
    REPAIR: 'NDT_REPAIR_LOG_REPAIR'
  };

  // All material categories state
  const [allMaterialCategories, setAllMaterialCategories] = useState<any[]>([]);
  
  // Tag management functions
  const getNDTTags = () => {
    const tagType = TAG_TYPES.NDT;
    if (!tagType) return [];
    
    return allMaterialCategories.filter((tag: any) => tag.tags_type === tagType);
  };

  // Convert NDT tags to options for the MultiSelect component
  const ndtTagOptions = useMemo(() => {
    const tagType = TAG_TYPES.NDT;
    if (!tagType) return [];
    
    const ndtTags = allMaterialCategories.filter((tag: any) => tag.tags_type === tagType);
    return ndtTags.map(tag => ({
      value: tag.tags_id.toString(),
      label: tag.tags_name
    }));
  }, [allMaterialCategories]);

  // Helper function to get options by tag type
  const getOptionsByTagType = (tagType: string) => {
    return allMaterialCategories
      .filter((tag: any) => tag.tags_type === tagType)
      .map((tag: any) => ({
        id: tag.tags_id.toString(),
        name: tag.tags_name
      }));
  };

  // Shift options - loaded from database
  const shiftOptions = useMemo(() => getOptionsByTagType(TAG_TYPES.SHIFT), [allMaterialCategories]);

  // Defect Type options - loaded from database
  const defectTypeOptions = useMemo(() => getOptionsByTagType(TAG_TYPES.DEFECT_TYPE), [allMaterialCategories]);

  // Location options - loaded from database
  const locationOptions = useMemo(() => getOptionsByTagType(TAG_TYPES.LOCATION), [allMaterialCategories]);

  // Repair options - loaded from database
  const repairOptions = useMemo(() => getOptionsByTagType(TAG_TYPES.REPAIR), [allMaterialCategories]);

  // Load all material categories when dialog opens
  useEffect(() => {
    const loadMaterialCategories = async () => {
      if (isOpen) {
        try {
          const allCategories = await getListMaterialCategory();
          if (allCategories && allCategories.length > 0) {
            setAllMaterialCategories(allCategories);
          }
        } catch (error) {
          console.error('Failed to load material categories:', error);
        }
      }
    };
    
    loadMaterialCategories();
  }, [isOpen]);



  const handleFileSelect = (files: FileList | null) => {
    if (files) {
      const fileArray = Array.from(files);
      setSelectedFiles(prev => [...prev, ...fileArray]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => {
      const newFiles = prev.filter((_, i) => i !== index);
      const newTags = { ...selectedTags };
      // Remove tags for this file and shift indices
      delete newTags[index];
      const updatedTags: any = {};
      Object.keys(newTags).forEach(key => {
        const keyNum = Number(key);
        if (keyNum > index) {
          updatedTags[keyNum - 1] = newTags[key];
        } else if (keyNum < index) {
          updatedTags[keyNum] = newTags[key];
        }
      });
      setSelectedTags(updatedTags);
      return newFiles;
    });
  };

  const handleTagsChange = (fileIndex: number, tags: string[]) => {
    const updatedTags = { ...selectedTags };
    updatedTags[fileIndex] = tags;
    setSelectedTags(updatedTags);
    setFormData(prev => ({
      ...prev,
      documentTags: Object.values(updatedTags)
    }));
  };

  const handleSave = async () => {

    try {
      const isUpdate = !!formData.id;
      
      // Initialize array to store all document IDs (existing + new)
      let ndtDocIds: number[] = [];
      setIsLoading(true);
      // Get existing document IDs if updating and documents exist
      if (isUpdate && initialData?.ndtIndicationDocId && Array.isArray(initialData.ndtIndicationDocId)) {
        // Extract docId from existing documents (they might be objects with docId property)
        const existingDocIds = initialData.ndtIndicationDocId
      
        ndtDocIds.push(...existingDocIds);
      }
      
      // Handle new document uploads
      if (selectedFiles && selectedFiles.length > 0) {
        const uploadPayload = {
          pipeId: Number(weld.selectedPipe?.pipeId),
          projectId: Number(weld.projectId),
          projectTypeId: Number(weld.selectedPipe?.projectTypeId),
          type: 'NDT'
        };
        
        const s3Response = await getPreSignedUrlToUpload(selectedFiles, uploadPayload, 1);

        if (s3Response && s3Response.length > 0) {
          const newDoc = {
            type: 'NDT',
            pipeId: Number(weld.selectedPipe?.pipeId),
            projectId: Number(weld.projectId),
            projectTypeId: Number(weld.selectedPipe?.projectTypeId),
            
            files: s3Response.map((file: any, index: number) => ({
              fileName: file.fileName,
              s3Key: file.s3Key,
              tagNames: getNDTTags().filter((tag: any) => selectedTags[index]?.includes(tag.tags_id.toString())).map((tag: any) => tag.tags_name),
              tags: selectedTags[index] ? selectedTags[index].map((tag: string) => Number(tag)) : []
            })),
            uploadDate: new Date().toISOString(),
            uploadedBy: user.email || 'no email'
          };

          const response = await AddSpecification([newDoc], false);
          if (response && response.data && response.data.length > 0) {
            const newDocIds = response.data.map((item: any) => Number(item.docId));
            ndtDocIds.push(...newDocIds);
          }
        }
      }

      const payload = {
        ...(isUpdate && { id: formData.id }),
        project_id: projectId,
        pipe_id: pipeId,
        weld_id: weldId,
        date: formData.date || dayjs().format('YYYY-MM-DD'),
        shift: formData.shift || '',
        weldersNo: formData.weldersNo || '',
        welderRepairNo: formData.welderRepairNo || '',
        location: formData.location || '',
        defectType: formData.defectType || '',
        acceptedWorkmanship: formData.acceptedWorkmanship === 'Yes',
        defectLocationStart: parseFloat(String(formData.defectLocationStart || '0')),
        defectLocationEnd: parseFloat(String(formData.defectLocationEnd || '0')),
        defectLength: parseFloat(String(formData.defectLength || '0')),
        defectDepth: parseFloat(String(formData.defectDepth || '0')),
        defectHeight: parseFloat(String(formData.defectHeight || '0')),
        comments: formData.comments || '',
        rootCause: formData.rootCause || '',
        documents: [],
        documentTags: formData.documentTags || [],
        pipeDia: formData.pipeDia || (weld as any).pipe_diameter || 20,
        wallThickness: (weld as any).wall_thickness || (weld as any).thickness || 5,
        wpsNumber: weld.wpsNumber || '',
        oclockLocation: formData.oclockLocation || '',
        repair: formData.repair || '',
        rca: formData.rca || '',
        criticalWeld: formData.criticalWeld || 'No',
        evaluation: formData.evaluation || 'Reject',
        passName: formData.passName || '',
        time: formData.time || new Date().toTimeString().split(' ')[0].substring(0, 5),
        correctiveActions: formData.correctiveActions || '',
        ndtIndicationDocId: ndtDocIds.length > 0 ? ndtDocIds.map((docId: number) => Number(docId)) : null
      };
      console.log('payload', payload);
      
      if (isUpdate) {
        dispatch(updateNDTRepairLog(payload));
      } else {
        dispatch(createNDTRepairLog(payload));
      }
      // Don't close immediately - wait for success via useEffect
    } catch (error) {
      console.error('Error saving repair log:', error);
    } finally {
      setTimeout(() => {
      setIsLoading(false);
      handleClose();
      }, 200);
      
    }
  };

  const handleAddShift = async (shiftName: string) => {
    if (shiftName.trim()) {
      if (shiftOptions.find((shift: any) => shift.name.toLowerCase() === shiftName.trim().toLowerCase())) {
        // Shift already exists
        return;
      }

      const apiPayload = {
        tags_name: shiftName.trim(),
        project_id: projectId,
        tags_type: TAG_TYPES.SHIFT
      };

      try {
        const res = await AddMaterialCategory(apiPayload, 'shift');
        if (res) {
          // Update all material categories
          setAllMaterialCategories(prev => [...prev, res]);
          
          // Automatically select the newly added shift
          setFormData(prev => ({
            ...prev,
            shift: res.tags_name
          }));
        }
      } catch (error) {
        console.error('Failed to add shift:', error);
      }
    }
    setIsAddingShift(false);
  };

  const handleAddDefectType = async (defectTypeName: string) => {
    if (defectTypeName.trim()) {
      if (defectTypeOptions.find((defectType: any) => defectType.name.toLowerCase() === defectTypeName.trim().toLowerCase())) {
        // Defect type already exists
        return;
      }

      const apiPayload = {
        tags_name: defectTypeName.trim(),
        project_id: projectId,
        tags_type: TAG_TYPES.DEFECT_TYPE
      };

      try {
        const res = await AddMaterialCategory(apiPayload, 'defect type');
        if (res) {
          // Update all material categories
          setAllMaterialCategories(prev => [...prev, res]);
          
          // Automatically select the newly added defect type
          setFormData(prev => ({
            ...prev,
            defectType: res.tags_name
          }));
        }
      } catch (error) {
        console.error('Failed to add defect type:', error);
      }
    }
    setIsAddingDefectType(false);
  };

  const handleAddLocation = async (locationName: string) => {
    if (locationName.trim()) {
      if (locationOptions.find((location: any) => location.name.toLowerCase() === locationName.trim().toLowerCase())) {
        // Location already exists
        return;
      }

      const apiPayload = {
        tags_name: locationName.trim(),
        project_id: projectId,
        tags_type: TAG_TYPES.LOCATION
      };

      try {
        const res = await AddMaterialCategory(apiPayload, 'location');
        if (res) {
          // Update all material categories
          setAllMaterialCategories(prev => [...prev, res]);
          
          // Automatically select the newly added location
          setFormData(prev => ({
            ...prev,
            location: res.tags_name
          }));
        }
      } catch (error) {
        console.error('Failed to add location:', error);
      }
    }
    setIsAddingLocation(false);
  };

  const handleAddRepair = async (repairName: string) => {
    if (repairName.trim()) {
      if (repairOptions.find((repair: any) => repair.name.toLowerCase() === repairName.trim().toLowerCase())) {
        // Repair already exists
        return;
      }

      const apiPayload = {
        tags_name: repairName.trim(),
        project_id: projectId,
        tags_type: TAG_TYPES.REPAIR
      };

      try {
        const res = await AddMaterialCategory(apiPayload, 'repair');
        if (res) {
          // Update all material categories
          setAllMaterialCategories(prev => [...prev, res]);
          
          // Automatically select the newly added repair
          setFormData(prev => ({
            ...prev,
            repair: res.tags_name
          }));
        }
      } catch (error) {
        console.error('Failed to add repair:', error);
      }
    }
    setIsAddingRepair(false);
  };

  const handleAddTag = async (newTagName: string) => {
    if (newTagName.trim()) {
      const tagType = TAG_TYPES.NDT;
      if (!tagType) {
        console.error('Invalid tag type for NDT');
        return;
      }

      // Check if tag already exists
      if (getNDTTags().find((tag: any) => tag.tags_name === newTagName.trim())) {
        console.warn(`${newTagName.trim()} tag already exists`);
        return;
      }

      const apiPayload = {
        tags_name: newTagName.trim(),
        project_id: projectId,
        tags_type: tagType
      };

      try {
        const res = await AddMaterialCategory(apiPayload, 'NDT tag');
        if (res) {
          // Update all material categories
          setAllMaterialCategories(prev => [...prev, res]);
          
          // Automatically select the newly added tag for the first file if it exists
          if (selectedFiles.length > 0) {
            const newTagValue = res.tags_id.toString();
            const fileIndex = 0;
            const updatedTags = { ...selectedTags };
            updatedTags[fileIndex] = [...(selectedTags[fileIndex] || []), newTagValue];
            setSelectedTags(updatedTags);
            handleTagsChange(fileIndex, updatedTags[fileIndex]);
          }
        }
      } catch (error) {
        console.error('Failed to add tag:', error);
      }
    }
    setIsAddingTag(false);
  };

  // Auto-calculate Defect Length
  useEffect(() => {
    const start = typeof formData.defectLocationStart === 'number' 
      ? formData.defectLocationStart 
      : (formData.defectLocationStart ? Number(formData.defectLocationStart) : null);
    const end = typeof formData.defectLocationEnd === 'number' 
      ? formData.defectLocationEnd 
      : (formData.defectLocationEnd ? Number(formData.defectLocationEnd) : null);

    if (start !== null && end !== null && !isNaN(start) && !isNaN(end) && end >= start) {
      const calculatedLength = end - start;
      setFormData(prev => ({
        ...prev,
        defectLength: calculatedLength
      }));
    } else if (start === null || end === null || isNaN(start) || isNaN(end)) {
      setFormData(prev => ({
        ...prev,
        defectLength: ''
      }));
    }
  }, [formData.defectLocationStart, formData.defectLocationEnd]);

  // Auto-calculate o'clock Location based on defect location start and end
  useEffect(() => {
    // Skip auto-calculation on initial load in edit mode to preserve saved o'clock location from backend
    if (isInitialLoad.current && initialData?.id) {
      return;
    }

    const start = typeof formData.defectLocationStart === 'number' 
      ? formData.defectLocationStart 
      : (formData.defectLocationStart ? Number(formData.defectLocationStart) : null);
    const end = typeof formData.defectLocationEnd === 'number' 
      ? formData.defectLocationEnd 
      : (formData.defectLocationEnd ? Number(formData.defectLocationEnd) : null);

    if (start !== null && end !== null && !isNaN(start) && !isNaN(end) && formData.pipeDia) {
      // Calculate o'clock position based on midpoint of defect location (start and end)
      // Assuming the defect location is measured along the pipe circumference
      // Convert to clock position (0-12 o'clock)
      // Use the midpoint (average) of start and end for the calculation
      const midpoint = (start + end) / 2;
      const pipeDiaInches = formData.pipeDia || 20;
      const circumference = Math.PI * pipeDiaInches * 25.4; // Convert inches to mm and calculate circumference
      const position = (midpoint / circumference) * 12;
      let oclock = Math.round(position) % 12;
      if (oclock === 0) oclock = 12;
      const finalOclock = 12 - oclock;
      setFormData(prev => ({
        ...prev,
        oclockLocation: `${finalOclock} o'clock`
      }));
    } else {
      // Only clear o'clock location if not in edit mode or if user explicitly cleared the values
      if (!initialData?.id) {
        setFormData(prev => ({
          ...prev,
          oclockLocation: ''
        }));
      }
    }
  }, [formData.defectLocationStart, formData.defectLocationEnd, formData.pipeDia, initialData?.id]);

  const handleClose = () => {
    // Reset form on close
    isInitialLoad.current = true; // Reset flag for next open
    setDate(dayjs().format('YYYY-MM-DD'));
    setFormData(getDefaultFormData());
    setSelectedTags({});
    setSelectedFiles([]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              variant="outline"
              type="button"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </Button>
            <DialogTitle className="text-2xl font-bold">
              {initialData ? 'Edit' : 'Add'} Repair Log {weldNumber && `- ${weldNumber}`}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Row 1: Date, Pipe Dia., Shift, Welders Number */}
          <div className="grid grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Date</Label>
              <div className="flex items-center space-x-2">
                <AntdDate
                  name="date"
                  value={formData.date || date}
                  onChange={(value: string) => {
                    setDate(value);
                    setFormData(prev => ({
                      ...prev,
                      date: value
                    }));
                  }}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pipeDia">Pipe Dia. (in)</Label>
              <Input
                id="pipeDia"
                value={formData.pipeDia || 20}
                disabled
                className="bg-gray-100 cursor-not-allowed"
                readOnly
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="shift">Shift</Label>
              <div className="relative mt-1">
                <SearchableSelect
                  value={formData.shift}
                  onValueChange={(value: string) => {
                    setFormData(prev => ({
                      ...prev,
                      shift: value
                    }));
                  }}
                  placeholder="Select shift"
                  searchPlaceholder="Search shifts..."
                  options={shiftOptions}
                  onAddNew={() => setIsAddingShift(true)}
                  addNewText="Add New Shift"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="weldersNo">Welders Number</Label>
              <Input
                id="weldersNo"
                value={formData.weldersNo || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  weldersNo: e.target.value
                }))}
                placeholder="Enter welders number"
              />
            </div>
          </div>

          {/* Row 2: Welder Repair Number, Location, Defect type, Repair */}
          <div className="grid grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="welderRepairNo">Welder Repair Number</Label>
              <Input
                id="welderRepairNo"
                value={formData.welderRepairNo || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  welderRepairNo: e.target.value
                }))}
                placeholder="Enter welder repair number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <div className="relative mt-1">
                <SearchableSelect
                  value={formData.location}
                  onValueChange={(value: string) => {
                    setFormData(prev => ({
                      ...prev,
                      location: value
                    }));
                  }}
                  placeholder="Select location"
                  searchPlaceholder="Search locations..."
                  options={locationOptions}
                  onAddNew={() => setIsAddingLocation(true)}
                  addNewText="Add New Location"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="defectType">Defect type</Label>
              <div className="relative mt-1">
                <SearchableSelect
                  value={formData.defectType}
                  onValueChange={(value: string) => {
                    setFormData(prev => ({
                      ...prev,
                      defectType: value
                    }));
                  }}
                  placeholder="Select defect type"
                  searchPlaceholder="Search defect types..."
                  options={defectTypeOptions}
                  onAddNew={() => setIsAddingDefectType(true)}
                  addNewText="Add New Defect Type"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="repair">Repair</Label>
              <div className="relative mt-1">
                <SearchableSelect
                  value={formData.repair}
                  onValueChange={(value: string) => {
                    setFormData(prev => ({
                      ...prev,
                      repair: value
                    }));
                  }}
                  placeholder="Select repair"
                  searchPlaceholder="Search repairs..."
                  options={repairOptions}
                  onAddNew={() => setIsAddingRepair(true)}
                  addNewText="Add New Repair"
                />
              </div>
            </div>
          </div>

          {/* Row 3: Defect Location Start, Defect Location End, o'clock Location, Defect Length */}
          <div className="grid grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="defectLocationStart">
                Defect Location Start (mm)
              </Label>
              <NumberInput
                id="defectLocationStart"
                value={formData.defectLocationStart}
                onChange={(value) => setFormData(prev => ({
                  ...prev,
                  defectLocationStart: value ? Number(value) : ''
                }))}
                placeholder="Enter start location in mm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="defectLocationEnd">
                Defect Location End (mm)
              </Label>
              <NumberInput
                id="defectLocationEnd"
                value={formData.defectLocationEnd}
                onChange={(value) => setFormData(prev => ({
                  ...prev,
                  defectLocationEnd: value ? Number(value) : ''
                }))}
                placeholder="Enter end location in mm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="oclockLocation">o'clock Location</Label>
              <Input
                id="oclockLocation"
                value={formData.oclockLocation || ''}
                disabled
                className="bg-gray-100 cursor-not-allowed"
                readOnly
                placeholder="Auto-calculated"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="defectLength">
                Defect Length (mm)
              </Label>
              <Input
                id="defectLength"
                value={formData.defectLength || ''}
                disabled
                className="bg-gray-100 cursor-not-allowed"
                readOnly
                placeholder="Auto-calculated"
              />
            </div>
          </div>

          {/* Row 4: Defect Depth, Defect Height, Accepted workmanship */}
          <div className="grid grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="defectDepth">
                Defect Depth (mm)
              </Label>
              <NumberInput
                id="defectDepth"
                value={formData.defectDepth}
                onChange={(value) => setFormData(prev => ({
                  ...prev,
                  defectDepth: value ? Number(value) : ''
                }))}
                placeholder="Enter defect depth in mm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="defectHeight">
                Defect Height (mm)
              </Label>
              <NumberInput
                id="defectHeight"
                value={formData.defectHeight}
                onChange={(value) => setFormData(prev => ({
                  ...prev,
                  defectHeight: value ? Number(value) : ''
                }))}
                placeholder="Enter defect height in mm"
              />
            </div>
          
            <div className="space-y-2">
              <Label htmlFor="passName">Pass Name</Label>
              <Input
                id="passName"
                value={formData.passName || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  passName: e.target.value
                }))}
                placeholder="Enter pass name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="time">Time</Label>
              <Input
                id="time"
                type="text"
                value={formData.time || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  time: e.target.value
                }))}
                placeholder="HH:MM"
              />
            </div>
            <div className="space-y-2"></div>
          </div>

          {/* Row 5: Pass Name, Time, Evaluation, Critical Weld */}
          <div className="grid grid-cols-4 gap-4">
          <div className="space-y-2">
              <Label>Accepted workmanship</Label>
              <div className="flex items-center gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    acceptedWorkmanship: 'Yes'
                  }))}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    formData.acceptedWorkmanship === 'Yes'
                      ? 'text-galvanizing-green bg-[rgba(31,154,58,0.08)] border-2'
                      : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                  }`}
                >
                  <ThumbsUp className="h-5 w-5" />
                  <span className="font-medium">Accept</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    acceptedWorkmanship: 'No'
                  }))}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    formData.acceptedWorkmanship === 'No'
                      ? 'text-readable-red bg-[rgba(196,0,31,0.06)] border-2'
                      : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                  }`}
                >
                  <ThumbsDown className="h-5 w-5" />
                  <span className="font-medium">Reject</span>
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Evaluation</Label>
              <div className="flex items-center gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    evaluation: 'Accept'
                  }))}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    formData.evaluation === 'Accept'
                      ? 'text-galvanizing-green bg-[rgba(31,154,58,0.08)] border-2'
                      : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                  }`}
                >
                  <ThumbsUp className="h-5 w-5" />
                  <span className="font-medium">Accept</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    evaluation: 'Reject'
                  }))}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    formData.evaluation === 'Reject'
                      ? 'text-readable-red bg-[rgba(196,0,31,0.06)] border-2'
                      : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                  }`}
                >
                  <ThumbsDown className="h-5 w-5" />
                  <span className="font-medium">Reject</span>
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Critical Weld</Label>
              <div className="flex items-center gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    criticalWeld: 'Yes'
                  }))}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    formData.criticalWeld === 'Yes'
                      ? 'text-blueprint-blue bg-[rgba(0,39,63,0.06)] border-2'
                      : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                  }`}
                >
                  <span className="font-medium">Yes</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    criticalWeld: 'No'
                  }))}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    formData.criticalWeld === 'No'
                      ? 'bg-gray-200 text-gray-700 border-2 border-gray-500'
                      : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                  }`}
                >
                  <span className="font-medium">No</span>
                </button>
              </div>
            </div>
          </div>

          {/* Row 6: Root Cause and Comments */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-base font-medium">Root Cause</Label>
              <textarea
                value={formData.rootCause}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  rootCause: e.target.value
                }))}
                className="w-full mt-2 p-3 border border-gray-300 min-h-[100px] rounded-md resize-none focus:outline-none focus:ring-1 focus:ring-blue-700 focus:"
                placeholder="Enter root cause..."
              />
            </div>
            <div className="space-y-2">
              <Label className="text-base font-medium">Corrective Actions</Label>
              <textarea
                value={formData.correctiveActions || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  correctiveActions: e.target.value
                }))}
                className="w-full mt-2 p-3 border border-gray-300 min-h-[100px] rounded-md resize-none focus:outline-none focus:ring-1 focus:ring-blue-700 focus:"
                placeholder="Enter corrective actions..."
              />
            </div>
           
          </div>

          {/* Row 7: Corrective Actions */}
          <div className="grid grid-cols-1 gap-4">
          <div className="space-y-2">
              <Label className="text-base font-medium">Comments</Label>
              <textarea
                value={formData.comments}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  comments: e.target.value
                }))}
                className="w-full mt-2 p-3 border border-gray-300 min-h-[100px] rounded-md resize-none focus:outline-none focus:ring-1 focus:ring-blue-700 focus:"
                placeholder="Enter comments..."
              />
            </div>
          </div>

          {/* Row 8: Document Upload */}
          <div className="space-y-2">
            <Label>Upload Documents</Label>
            <p className="text-sm text-gray-600">
              Upload repair log documents.
              <br />
              <span className="text-xs text-gray-500">
                Allowed: PDF, Word (.doc, .docx), Excel (.xls, .xlsx), Text (.txt), Images (.jpg, .png, .gif)
                <br />
                Maximum file size: 100MB per file
              </span>
            </p>
            <FileUpload
              onFileSelect={handleFileSelect}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
              multiple={true}
              maxFiles={100}
              buttonText="Upload Documents"
              variant="outline"
              size="lg"
              onRemoveFile={handleRemoveFile}
              selectedFiles={selectedFiles}
              showFileList={selectedFiles.length > 0}
              className="w-full"
              showTags={true}
              optionsTags={ndtTagOptions}
              selectedTags={selectedTags}
              showAddButton={true}
              addButtonText="Add New Tag"
              onAddButtonClick={() => setIsAddingTag(true)}
              onTagsChange={handleTagsChange}
              maxHeight=""
            />
            {selectedFiles.length > 0 && (
              <div className="text-xs text-gray-500">
                Current files: {selectedFiles.length} file(s)
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSaving || isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || isLoading}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isSaving || isLoading ? (initialData ? 'Updating...' : 'Saving...') : (initialData ? 'Update Repair Log' : 'Save Repair Log')}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Add New Shift Dialog */}
      {isAddingShift && (
        <AddNewItemDialog
          open={isAddingShift}
          onOpenChange={setIsAddingShift}
          title="Add New Shift"
          label="Shift"
          placeholder="Enter shift name"
          onAdd={handleAddShift}
          buttonText="Add Shift"
          cancelText="Cancel"
        />
      )}

      {/* Add New Defect Type Dialog */}
      {isAddingDefectType && (
        <AddNewItemDialog
          open={isAddingDefectType}
          onOpenChange={setIsAddingDefectType}
          title="Add New Defect Type"
          label="Defect Type"
          placeholder="Enter defect type name"
          onAdd={handleAddDefectType}
          buttonText="Add Defect Type"
          cancelText="Cancel"
        />
      )}

      {/* Add New Location Dialog */}
      {isAddingLocation && (
        <AddNewItemDialog
          open={isAddingLocation}
          onOpenChange={setIsAddingLocation}
          title="Add New Location"
          label="Location"
          placeholder="Enter location name"
          onAdd={handleAddLocation}
          buttonText="Add Location"
          cancelText="Cancel"
        />
      )}

      {/* Add New Repair Dialog */}
      {isAddingRepair && (
        <AddNewItemDialog
          open={isAddingRepair}
          onOpenChange={setIsAddingRepair}
          title="Add New Repair"
          label="Repair"
          placeholder="Enter repair name"
          onAdd={handleAddRepair}
          buttonText="Add Repair"
          cancelText="Cancel"
        />
      )}

      {/* Add New Tag Dialog */}
      {isAddingTag && (
        <AddNewItemDialog
          open={isAddingTag}
          onOpenChange={setIsAddingTag}
          title="Add New NDT Tag"
          label="Tag"
          placeholder="Enter tag name"
          onAdd={handleAddTag}
          buttonText="Add Tag"
          cancelText="Cancel"
        />
      )}

    </Dialog>
  );
};

